import {
  addDays,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { PlusIcon } from "lucide-react";
import {
  Fragment,
  type KeyboardEvent,
  type PointerEvent,
  type PointerEventHandler,
  type ReactNode,
  type Ref,
} from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { calendarDayKey, calendarEventsByDay } from "./record-calendar-index";

/*
 * The channel calendar at rest, as markup. RecordCalendarLens keeps the
 * records query, the URL position and editing, and the month grid keeps
 * dragging; both render through these, so fixture events compose the same
 * month. No hooks and no directive: a Server Component draws the month, so
 * its dates are worked out once, where the page renders.
 */

/** What the calendar draws of an event: its title and colour over its period. */
export type CalendarEventView = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  /** The event's dot, as a CSS colour. */
  color: string;
};

export type CalendarMonthDay<T extends CalendarEventView = CalendarEventView> = {
  /** yyyy-MM-dd, as `data-calendar-day` names the cell. */
  key: string;
  date: Date;
  /** The day falls in the month shown, not the weeks around it. */
  inMonth: boolean;
  /** The day's events, all-day ones first. */
  events: T[];
};

export type CalendarMonth<T extends CalendarEventView = CalendarEventView> = {
  /** The weekday headings, from the week's first day. */
  weekdays: string[];
  /** Six weeks of days from the week the month starts in. */
  days: CalendarMonthDay<T>[];
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_DAYS = 6 * 7;
/** A day cell lists this many events before a "+n more" popover. */
const DAY_EVENT_LIMIT = 4;

/** The month around `anchor` as the grid draws it, each day holding the events that cover it. */
export function calendarMonth<T extends CalendarEventView>(
  anchor: Date,
  weekStartsOn: 0 | 1,
  events: readonly T[],
): CalendarMonth<T> {
  const gridStart = startOfWeek(startOfMonth(anchor), { weekStartsOn });
  const dates = Array.from({ length: MONTH_DAYS }, (_, index) =>
    addDays(gridStart, index),
  );
  const eventsByDay = calendarEventsByDay(events, dates[0]!, dates.at(-1)!);
  return {
    weekdays:
      weekStartsOn === 1 ? [...WEEKDAYS.slice(1), WEEKDAYS[0]!] : WEEKDAYS,
    days: dates.map((date) => {
      const key = calendarDayKey(date);
      const indexed = eventsByDay.get(key);
      return {
        key,
        date,
        inMonth: isSameMonth(date, anchor),
        events: [...(indexed?.allDay ?? []), ...(indexed?.timed ?? [])],
      };
    }),
  };
}

/** The calendar's frame: the controls row, any notes, then the period; later children dim while it refreshes. */
export function RecordCalendarLensView({
  refreshing = false,
  children,
}: {
  refreshing?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col bg-background transition-opacity",
        refreshing && "[&>*:not(:first-child)]:opacity-60",
      )}
      aria-busy={refreshing || undefined}
    >
      {children}
    </div>
  );
}

/** Why the events cannot be dragged, between the controls and the period: their dates are kept elsewhere. */
export function RecordCalendarLockedNoteView({ reason }: { reason: string }) {
  return (
    <p
      className="border-b border-border bg-muted/35 px-3 py-1.5 text-xs text-muted-foreground"
      role="note"
    >
      {reason} Add Twin-owned scheduling fields or connector
      write-through to enable dragging.
    </p>
  );
}

/** The month grid: weekday headings over six weeks of days, each with up to four events and a "+n more" popover. */
export function RecordCalendarMonthView<T extends CalendarEventView>({
  month,
  today = null,
  dropDayKey = null,
  gridRef,
  onGridPointerLeave,
  onCreate,
  onOpen,
  renderEvent,
}: {
  month: CalendarMonth<T>;
  /** The day circled as today. */
  today?: Date | null;
  /** The day a dragged event would land on. */
  dropDayKey?: string | null;
  gridRef?: Ref<HTMLDivElement>;
  onGridPointerLeave?: PointerEventHandler<HTMLDivElement>;
  /** An empty part of a day, or its add button, was clicked. */
  onCreate?: (day: Date) => void;
  onOpen?: (event: T) => void;
  /** Draws a day's event; plain event buttons otherwise. */
  renderEvent?: (event: T) => ReactNode;
}) {
  return (
    <div className="mlx-floating-panel-scroll-surface min-w-[760px] flex-1 overflow-auto bg-background">
      <div className="sticky top-0 z-10 grid grid-cols-7 border-b border-border bg-background/95 backdrop-blur-sm">
        {month.weekdays.map((day) => (
          <div
            key={day}
            className="px-2 py-2 text-right text-2xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            {day}
          </div>
        ))}
      </div>
      <div
        ref={gridRef}
        className="grid min-h-[720px] grid-cols-7 grid-rows-6"
        onPointerLeave={onGridPointerLeave}
      >
        {month.days.map((day) => (
          <div
            key={day.key}
            data-calendar-day={day.key}
            className={cn(
              "group/day relative min-h-28 border-b border-r border-border/70 p-1.5 transition-colors",
              !day.inMonth && "bg-muted/20",
              dropDayKey === day.key &&
                "bg-primary/[0.06] ring-2 ring-inset ring-primary/30",
            )}
            onClick={
              onCreate
                ? (clickEvent) => {
                    if ((clickEvent.target as HTMLElement).closest("button, a"))
                      return;
                    onCreate(day.date);
                  }
                : undefined
            }
          >
            <div className="mb-1 flex items-center justify-between">
              <button
                type="button"
                aria-label={`Add record on ${format(day.date, "d MMMM")}`}
                className="flex size-5 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/day:opacity-100"
                onClick={onCreate ? () => onCreate(day.date) : undefined}
              >
                <PlusIcon className="size-3.5" aria-hidden />
              </button>
              <span
                className={cn(
                  "flex size-6 items-center justify-center rounded-full text-xs tabular-nums text-muted-foreground",
                  today !== null &&
                    isSameDay(day.date, today) &&
                    "bg-primary font-semibold text-primary-foreground",
                )}
              >
                {format(day.date, "d")}
              </span>
            </div>
            <div className="space-y-0.5">
              {day.events.slice(0, DAY_EVENT_LIMIT).map((event) =>
                renderEvent ? (
                  <Fragment key={event.id}>{renderEvent(event)}</Fragment>
                ) : (
                  <CalendarEventButtonView
                    key={event.id}
                    event={event}
                    onOpen={onOpen}
                  />
                ),
              )}
              {day.events.length > DAY_EVENT_LIMIT ? (
                <CalendarDayOverflowView
                  day={day.date}
                  events={day.events}
                  onOpen={onOpen}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A day's "+n more": a popover listing every event on the day. */
function CalendarDayOverflowView<T extends CalendarEventView>({
  day,
  events,
  onOpen,
}: {
  day: Date;
  events: T[];
  onOpen?: (event: T) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            className="w-full rounded px-1.5 pt-0.5 text-left text-2xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Show all ${events.length} events on ${format(day, "d MMMM")}`}
          />
        }
      >
        +{events.length - DAY_EVENT_LIMIT} more
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <p className="px-1.5 pb-1.5 text-xs font-semibold">
          {format(day, "EEEE, d MMMM")}
        </p>
        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          {events.map((event) => (
            <CalendarEventButtonView
              key={event.id}
              event={event}
              onOpen={onOpen}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** An event as a day lists it: its dot, its start time unless all day, and its title. */
export function CalendarEventButtonView<T extends CalendarEventView>({
  event,
  onOpen,
  showTime = true,
  className,
  ...handlers
}: {
  event: T;
  onOpen?: (event: T) => void;
  showTime?: boolean;
  className?: string;
  onPointerDown?: (pointerEvent: PointerEvent<HTMLButtonElement>) => void;
  onPointerMove?: (pointerEvent: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp?: (pointerEvent: PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel?: (pointerEvent: PointerEvent<HTMLButtonElement>) => void;
  onLostPointerCapture?: (pointerEvent: PointerEvent<HTMLButtonElement>) => void;
  onKeyDown?: (keyEvent: KeyboardEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "group flex h-6 w-full touch-none items-center gap-1.5 rounded-md px-1.5 text-left text-2xs font-medium text-foreground outline-none transition-[background-color,transform,opacity] duration-150 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]",
        className,
      )}
      title={event.title}
      onClick={onOpen ? () => onOpen(event) : undefined}
      {...handlers}
    >
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: event.color }}
      />
      {showTime && !event.allDay ? (
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {format(event.start, "HH:mm")}
        </span>
      ) : null}
      <span className="truncate">{event.title}</span>
    </button>
  );
}
